import {
  Badge,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useSnapshot } from 'valtio';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { useCheckPermission } from '@duke-hq/app-admin-web/hooks/useCheckPermission';
import type { FlowActionKind } from './classifyFlowChange';
import { loadInspectorChrome, saveInspectorChrome } from './inspectorChromeStorage';
import {
  undoInspectorState,
  type InspectorLogLine,
  type InspectorReason,
  type InspectorStackEntry,
} from './undoInspectorState';

declare global {
  interface Window {
    // Console toggle for the undo inspector; registered for dev-tools users only.
    undoInspector?: () => void;
  }
}

// -- Undo/redo inspector panel --------------------------------------------------
// A floating dev instrument over the Flow Editor canvas. Shows the live undo
// (past) and redo (future) stacks, a "you are here" divider between them, and a
// rolling log of every recorder decision. Each entry is named by the inferred
// action (message edit, option reorder, delete node, ...), so a missing or wrong
// entry is obvious at a glance. Developer-only; summoned with window.undoInspector().
//
// It reads the dedicated undoInspectorState (not flowEditorState) via useSnapshot
// and renders outside the canvas, so it never re-renders nodes and cannot perturb
// the keystroke-performance path it observes. All window chrome (drag position,
// minimised) is panel-local and persisted to localStorage; it never touches the
// stores, so the canvas re-render guarantee holds.

const PANEL_WIDTH = 360;
const PANEL_MARGIN = 16;

// Keep a dragged position inside the current viewport. A position saved when the
// window was wider (or before a resize) could otherwise place the panel entirely
// off-screen, where it can't be dragged back. Clamp so at least the header stays
// reachable. Reserve enough height that the draggable header is always on screen
// even when the panel itself is taller than the window.
function clampPosition(pos: { x: number; y: number }): { x: number; y: number } {
  const maxX = Math.max(PANEL_MARGIN, window.innerWidth - PANEL_WIDTH - PANEL_MARGIN);
  const maxY = Math.max(PANEL_MARGIN, window.innerHeight - PANEL_MARGIN * 4);
  return {
    x: Math.min(Math.max(PANEL_MARGIN, pos.x), maxX),
    y: Math.min(Math.max(PANEL_MARGIN, pos.y), maxY),
  };
}

const REASON_COLOUR: Record<InspectorReason, string> = {
  recorded: 'green',
  'gated-noop': 'gray',
  'text-run-coalesced': 'blue',
  'retracted-noop-run': 'yellow',
  'suppressed-restore': 'purple',
  undo: 'orange',
  redo: 'teal',
};

// Short labels keep the badge column narrow so the log stays columnar.
const REASON_LABEL: Record<InspectorReason, string> = {
  recorded: 'record',
  'gated-noop': 'no-op',
  'text-run-coalesced': 'coalesce',
  'retracted-noop-run': 'retract',
  'suppressed-restore': 'skip·rest',
  undo: 'UNDO',
  redo: 'REDO',
};

// The restore/no-op events pair with every undo/redo and are mostly noise when
// reading the sequence of real edits; the log filter hides them by default.
const NOISE_REASONS = new Set<InspectorReason>(['suppressed-restore', 'gated-noop']);

// Rail colour by action kind: destructive kinds pop red, additive green, content
// edits blue, persona purple, media/connect teal, neutral moves grey.
const KIND_COLOUR: Record<FlowActionKind, string> = {
  'add-node': 'green.400',
  'duplicate-node': 'green.400',
  'delete-node': 'red.400',
  'option-remove': 'red.400',
  disconnect: 'red.400',
  rename: 'blue.400',
  'option-edit': 'blue.400',
  'option-add': 'blue.400',
  'option-reorder': 'blue.400',
  'message-edit': 'blue.400',
  'message-add': 'green.400',
  'message-delete': 'red.400',
  'message-move': 'gray.400',
  'node-move': 'gray.400',
  edit: 'gray.400',
  'persona-change': 'purple.400',
  'unset-persona': 'purple.400',
  'media-insert': 'teal.400',
  'media-remove': 'red.400',
  'link-edit': 'blue.400',
  connect: 'teal.400',
};

function StackRow({
  entry,
  isHead,
  dimmed,
}: {
  entry: InspectorStackEntry;
  isHead?: boolean;
  dimmed?: boolean;
}) {
  return (
    <Box
      px={2}
      py={1}
      borderLeftWidth="2px"
      borderLeftColor={KIND_COLOUR[entry.action.kind] ?? 'gray.400'}
      borderRadius="sm"
      bg={isHead ? 'bg.emphasized' : 'bg.subtle'}
      opacity={dimmed ? 0.6 : 1}
    >
      <HStack justify="space-between" align="baseline">
        <HStack gap={1} align="baseline">
          <Text fontSize="xs" fontWeight="semibold">
            {entry.action.label}
          </Text>
          {isHead ? (
            <Text fontSize="2xs" color="brand.600" fontWeight="bold">
              ← HEAD
            </Text>
          ) : null}
        </HStack>
        <Text fontSize="2xs" color="fg.muted" whiteSpace="nowrap">
          {entry.nodeCount}n · {entry.edgeCount}e
        </Text>
      </HStack>
      {entry.action.summary ? (
        <Text fontSize="2xs" color="fg.muted" fontFamily="mono" wordBreak="break-word">
          {entry.action.summary}
        </Text>
      ) : null}
      <Text fontSize="2xs" color="fg.subtle" fontFamily="mono" wordBreak="break-word">
        → [{entry.restoresTo}]
      </Text>
    </Box>
  );
}

function LogRow({ line }: { line: InspectorLogLine }) {
  return (
    <Box
      display="grid"
      gridTemplateColumns={`${pxToRem(28)} ${pxToRem(72)} 1fr auto`}
      gap={2}
      alignItems="baseline"
      fontFamily="mono"
      fontSize="2xs"
      px={1}
      py="0.5"
      _hover={{ bg: 'bg.subtle' }}
    >
      <Text color="fg.muted">{line.seq}</Text>
      <Badge colorScheme={REASON_COLOUR[line.reason]} fontSize="2xs" justifySelf="start">
        {REASON_LABEL[line.reason]}
      </Badge>
      <Text wordBreak="break-word">
        {line.action ? `${line.action.label} ${line.action.summary}` : '—'}
      </Text>
      <Text color="fg.muted" whiteSpace="nowrap">
        p{line.pastDepth} f{line.futureDepth}
      </Text>
    </Box>
  );
}

export function UndoInspectorPanel() {
  // Developer-only instrument: gate behind the same permission as the rest of the
  // admin developer tooling, so it never appears for ordinary admins. The hook
  // seeds synchronously from the SSR permission set, so this is decided on first
  // render with no flash.
  const canAccessDeveloperTools = useCheckPermission('canAccessDeveloperTools');
  const snap = useSnapshot(undoInspectorState);
  const [copied, setCopied] = useState(false);
  const [hideNoise, setHideNoise] = useState(true);

  const initialChrome = useMemo(() => loadInspectorChrome(), []);
  // Clamp the stored position to the current viewport on load: a position saved in
  // a wider window must not start off-screen (where it can't be dragged back).
  const [position, setPosition] = useState(
    initialChrome.position ? clampPosition(initialChrome.position) : null,
  );
  const dragState = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(
    null,
  );

  // Persist the dragged position and open/closed state whenever either settles, so
  // the panel reopens where (and whether) it was last left.
  useEffect(() => {
    saveInspectorChrome({ position, open: snap.enabled });
  }, [position, snap.enabled]);

  // Keep the panel on screen when the window resizes (e.g. a narrower viewport
  // would otherwise leave a previously-dragged panel off the right edge).
  useEffect(() => {
    const onResize = () => {
      setPosition((current) => (current ? clampPosition(current) : null));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // window.undoInspector() toggles the panel. Registered for dev-tools users only,
  // so the handle is absent for everyone else. Also restore the last open/closed
  // state here (dev-only, so it never auto-opens for an ordinary admin even if the
  // stored flag somehow says open), so a developer who leaves it open finds it open.
  useEffect(() => {
    if (!canAccessDeveloperTools) {
      return;
    }
    if (initialChrome.open) {
      undoInspectorState.setEnabled(true);
    }
    window.undoInspector = () => undoInspectorState.toggle();
    return () => {
      delete window.undoInspector;
    };
  }, [canAccessDeveloperTools, initialChrome.open]);

  const copyReport = () => {
    void navigator.clipboard.writeText(undoInspectorState.formatReport()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  // Drag by the header: track the pointer and translate the panel, clamped to the
  // viewport so it can't be dragged off-screen. Pointer capture keeps the drag
  // alive even if the cursor outruns the header.
  const onHeaderPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const panelLeft = position?.x ?? rect.left;
    const panelTop = position?.y ?? rect.top;
    dragState.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: panelLeft,
      y: panelTop,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onHeaderPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = dragState.current;
    if (!start) {
      return;
    }
    const dx = event.clientX - start.pointerX;
    const dy = event.clientY - start.pointerY;
    setPosition(clampPosition({ x: start.x + dx, y: start.y + dy }));
  };

  const onHeaderPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragState.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  if (!canAccessDeveloperTools) {
    return null;
  }

  // When dragged, anchor by computed left/top; otherwise the default top-right.
  // These are drag-computed pixel offsets (like a React Flow transform), not
  // authored layout values, so raw px here does not breach the scale rule.
  const anchorStyle = position
    ? { left: position.x, top: position.y, right: 'auto' as const }
    : { right: PANEL_MARGIN, top: PANEL_MARGIN };

  // Nothing renders until summoned from the console (window.undoInspector()).
  if (!snap.enabled) {
    return null;
  }

  // Past is bottom -> top (oldest first); show newest on top, like a stack. The
  // top of the past stack is the current HEAD.
  const pastTopFirst = [...snap.past].reverse();
  const logLines = hideNoise ? snap.log.filter((l) => !NOISE_REASONS.has(l.reason)) : snap.log;
  const logNewestFirst = [...logLines].reverse();
  const hiddenCount = snap.log.length - logLines.length;

  return (
    <Box
      position="absolute"
      zIndex={1000}
      style={anchorStyle}
      w={pxToRem(PANEL_WIDTH)}
      maxH="80vh"
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      boxShadow="lg"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      data-testid="undo-inspector-panel"
    >
      <HStack justify="space-between" px={3} py={2} borderBottomWidth="1px" bg="bg.muted">
        {/* Only the title is the drag handle. Putting the pointer-capture on the
            whole header swallowed every button's click (the capture stole the
            pointer before the button could fire it). */}
        <Text
          fontSize="sm"
          fontWeight="bold"
          flex="1"
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
        >
          ⠿ Undo inspector
        </Text>
        <HStack gap={1}>
          <Button size="2xs" variant="ghost" onClick={copyReport}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button size="2xs" variant="ghost" onClick={() => undoInspectorState.clear()}>
            Clear
          </Button>
          <CloseButton size="sm" onClick={() => undoInspectorState.setEnabled(false)} />
        </HStack>
      </HStack>

      <HStack px={3} py={2} borderBottomWidth="1px" gap={2} justify="space-between">
        <Checkbox
          size="sm"
          isChecked={!snap.autosaveDisabled}
          onChange={(e) => {
            undoInspectorState.autosaveDisabled = !e.target.checked;
          }}
        >
          <Text fontSize="xs">Autosave</Text>
        </Checkbox>
        <Text fontSize="2xs" color="fg.muted">
          {snap.lastSavePayloadBytes != null
            ? `${Math.round(snap.lastSavePayloadBytes / 1024)}KB · ${snap.lastSaveDurationMs ?? '?'}ms`
            : 'Cmd+S saves now'}
        </Text>
      </HStack>

      <Box overflowY="auto" p={3}>
        <VStack align="stretch" gap={1}>
          <Text fontSize="2xs" fontWeight="bold" color="fg.muted" textTransform="uppercase">
            Redo (future) — {snap.future.length}
          </Text>
          {snap.future.length === 0 ? (
            <Text fontSize="xs" color="fg.muted">
              (empty)
            </Text>
          ) : (
            snap.future.map((entry, i) => (
              <StackRow
                key={`f-${i}-${entry.action.kind}-${entry.action.summary}`}
                entry={entry}
                dimmed
              />
            ))
          )}

          <Flex align="center" gap={2} my={1}>
            <Box flex={1} h={pxToRem(1)} bg="border.emphasized" />
            <Text fontSize="2xs" fontWeight="bold" color="brand.600">
              YOU ARE HERE
            </Text>
            <Box flex={1} h={pxToRem(1)} bg="border.emphasized" />
          </Flex>

          <Text fontSize="2xs" fontWeight="bold" color="fg.muted" textTransform="uppercase">
            Undo (past) — {snap.past.length}
          </Text>
          {pastTopFirst.length === 0 ? (
            <Text fontSize="xs" color="fg.muted">
              (empty)
            </Text>
          ) : (
            pastTopFirst.map((entry, i) => (
              <StackRow
                key={`p-${i}-${entry.action.kind}-${entry.action.summary}`}
                entry={entry}
                isHead={i === 0}
              />
            ))
          )}
        </VStack>

        <HStack justify="space-between" mt={4} mb={1}>
          <Text fontSize="2xs" fontWeight="bold" color="fg.muted" textTransform="uppercase">
            Recorder log
          </Text>
          <Checkbox
            size="sm"
            isChecked={hideNoise}
            onChange={(e) => setHideNoise(e.target.checked)}
          >
            <Text fontSize="2xs" color="fg.muted">
              Hide restore noise {hiddenCount > 0 ? `(${hiddenCount})` : ''}
            </Text>
          </Checkbox>
        </HStack>
        <VStack align="stretch" gap={0}>
          {logNewestFirst.map((line) => (
            <LogRow key={line.seq} line={line} />
          ))}
        </VStack>
      </Box>
    </Box>
  );
}
